import { z } from 'zod';
import { TaskStatusTypeEnum } from '../task-status';
import { ModuleTypeEnum } from '../module';
import { ModuleReferenceInfo } from '../module-reference';
import { createResponseSchema, createPaginatedResponseSchema } from '../pagination';
import { StatusConfigSchema } from '../templates';
import { CaptureRefSchema } from '../capture';

// Task priority enum
export enum TaskPriorityEnum {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

// Core Task schema with all base fields
export const TaskSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  
  // Polymorphic relationship fields
  moduleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  groupId: z.string().uuid().nullable().optional(),
  
  // Task fields
  taskNumber: z.string().nullable().optional(), // e.g., "YOLK-134"
  name: z.string(),
  description: z.string().nullable().optional(),
  status: z.string().default(TaskStatusTypeEnum.NOT_STARTED),
  statusTypeId: z.nativeEnum(TaskStatusTypeEnum).nullable().optional(),
  // Modular task type (Bug, Feature, ...). Set from default when omitted.
  taskTypeDefId: z.string().uuid().nullable().optional(),
  // Lightweight type summary for display (icon/color), populated on reads.
  taskTypeDef: z
    .object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
      icon: z.string().nullable().optional(),
      color: z.string().nullable().optional(),
      // 'LETTER' renders a colored initial chip; 'ICON' renders the icon.
      displayStyle: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  priority: z.string().default(TaskPriorityEnum.MEDIUM),
  scheduledFor: z.date().nullable().optional(),
  dueDate: z.date().nullable().optional(),
  isAllDay: z.boolean().default(true),
  // Whether dueDate carries a user-set time of day. isAllDay is the
  // (inverted) equivalent for scheduledFor.
  dueDateHasTime: z.boolean().default(false),
  slaDueAt: z.date().nullable().optional(),
  slaBreachedAt: z.date().nullable().optional(),
  completedAt: z.date().nullable().optional(),
  completedBy: z.string().uuid().nullable().optional(),
  order: z.string().default("a1"),
  isArchived: z.boolean().default(false),
  isPrivate: z.boolean().default(false), // Private tasks only show in my-tasks, not in project task lists
  settings: z.any().default({}),
  createdBy: z.string().uuid().nullable().optional(),
  // T1 — capture provenance (U7 badge). Server-managed, write-once at
  // ingestion; null for records created before T1 or outside a wired producer.
  captureId: z.string().uuid().nullable().optional(),
  capture: CaptureRefSchema.nullable().optional(),
  // Source-note provenance. Set when the task was created via a note's
  // "Extract Action Items" flow; light embed for the task detail back-link.
  sourceNoteId: z.string().uuid().nullable().optional(),
  sourceNote: z
    .object({
      id: z.string().uuid(),
      title: z.string(),
      deletedAt: z.date().nullable().optional(),
    })
    .nullable()
    .optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  moduleInfo: z.any().nullable().optional(),
  // Workstream membership provenance: indexes into the workstream's
  // membershipRules.sources that matched this task. Populated only by the
  // workstream tasks endpoint ("why is this task here").
  workstreamSourceIndexes: z.array(z.number()).optional(),
  
  // Subtask fields
  parentTaskId: z.string().uuid().nullable().optional(),
  depth: z.number().default(0),
  path: z.string().nullable().optional(),
  subtaskCount: z.number().default(0).optional(), // Count of direct subtasks
  dependencyCount: z.number().default(0).optional(), // Count of total dependencies (from + to)
  
  // Recurring task fields
  isRecurring: z.boolean().default(false),
  recurringPattern: z.any().nullable().optional(), // Properly typed in the main export
  recurringEndDate: z.date().nullable().optional(),
  nextRecurrenceDate: z.date().nullable().optional(),
  parentRecurringTaskId: z.string().uuid().nullable().optional(),
  recurringInstanceNum: z.number().nullable().optional(),
  isRecurringTemplate: z.boolean().default(false),
  
  // Estimation fields
  estimatedHours: z.number().nullable().optional(), // Estimated hours to complete the task
  
  // Sprint-related fields with velocity tracking
  sprintId: z.string().uuid().nullable().optional(),
  storyPoints: z.number().nullable().optional(),
  sprintOrder: z.number().nullable().optional(),

  // Personal accounts (family daily-habit loop): star reward for completing
  // this chore-style task. Null/absent for normal tasks; honored server-side
  // only in PERSONAL orgs.
  starValue: z.number().int().nullable().optional(),

  // Cycle-related fields
  cycleInstanceId: z.string().uuid().nullable().optional(),

  // Bucket-related fields
  bucketId: z.string().uuid().nullable().optional(),
  bucketOrder: z.number().nullable().optional(),

  // Velocity tracking fields
  originalEstimate: z.number().nullable().optional(), // Original story point estimate
  finalEstimate: z.number().nullable().optional(), // Final story point estimate after completion
  estimateAccuracy: z.number().nullable().optional(), // How accurate the original estimate was (0-100%)
  cycleTimeHours: z.number().nullable().optional(), // Time from start to completion in hours
  velocityContribution: z.number().nullable().optional(), // Actual points contributed to sprint velocity
  wasCarryOver: z.boolean().default(false), // Whether task was carried over from previous sprint
  wasAddedMidSprint: z.boolean().default(false), // Whether task was added during active sprint
  blockedTimeHours: z.number().nullable().optional(), // Time task was blocked
  reworkCount: z.number().default(0), // Number of times task needed rework
  startedAt: z.date().nullable().optional(), // When task was started
});

export type Task = z.infer<typeof TaskSchema>;

// Create task schema
export const CreateTaskSchema = TaskSchema.omit({
  id: true,
  organizationId: true, // Organization ID is set from context in backend
  createdBy: true, // Created by is set from authenticated user context in backend
  createdAt: true,
  updatedAt: true,
  completedAt: true,
  isArchived: true,
  settings: true,
  status: true,
  recurringInstanceNum: true,
  nextRecurrenceDate: true,
  depth: true, // Depth is calculated automatically
  path: true, // Path is calculated automatically
}).extend({
  organizationId: z.string().uuid().optional(), // Optional since backend sets it from context
  settings: z.any().optional(),
  assignees: z.array(z.string()).optional(),
  customFields: z.record(z.any()).optional(),
  status: z.string().optional(), // Optional status to set initial status based on project type
  statusTypeId: z.nativeEnum(TaskStatusTypeEnum).optional(),
});

export type CreateTask = z.infer<typeof CreateTaskSchema>;

// Update task schema
export const UpdateTaskSchema = TaskSchema.omit({
  id: true,
  organizationId: true,
  moduleType: true,
  moduleId: true,
  createdAt: true,
  updatedAt: true,
  recurringInstanceNum: true,
  statusTypeId: true, // Don't allow direct statusTypeId updates - must go through status update
}).partial().extend({
  customFields: z.record(z.any()).optional(),
  assignees: z.array(z.string()).optional(),
});

export type UpdateTask = z.infer<typeof UpdateTaskSchema>;

// Update Task input schema/type for API + UI payloads
export const UpdateTaskInputSchema = UpdateTaskSchema;

export type UpdateTaskInput = z.infer<typeof UpdateTaskInputSchema>;

// Configurable task schema with relations
export function createTaskWithRelations<T extends Record<string, z.ZodType>>(relations: T) {
  return TaskSchema.extend({
    customFields: z.record(z.any()).optional(),
    ...relations,
  });
}

// Pre-defined relation schemas
export const TaskRelations = {
  taskStatus: z.any().optional(),
  assignees: z.array(z.any()).optional(),
  module: z.any().optional(),
  sprint: z.any().optional(),
  group: z.any().optional(),
  parentTask: z.any().optional(),
  subtasks: z.array(z.lazy(() => TaskSchema)).optional(),
  dependenciesFrom: z.array(z.any()).optional(),
  dependenciesTo: z.array(z.any()).optional(),
  projectSettings: z.object({
    globalStatuses: z.array(StatusConfigSchema).optional(),
  }).optional(),
};

// Common task variants using the factory
export const TaskWithStatusSchema = createTaskWithRelations({
  taskStatus: TaskRelations.taskStatus,
});

export const TaskWithAssigneesSchema = createTaskWithRelations({
  assignees: TaskRelations.assignees,
});

export const TaskWithModuleSchema = createTaskWithRelations({
  module: TaskRelations.module,
});

export const TaskWithSprintSchema = createTaskWithRelations({
  sprint: TaskRelations.sprint,
});

export const TaskWithRelationsSchema = createTaskWithRelations(TaskRelations);

// Export types
export type TaskWithStatus = z.infer<typeof TaskWithStatusSchema>;
export type TaskWithAssignees = z.infer<typeof TaskWithAssigneesSchema>;
export type TaskWithModule = z.infer<typeof TaskWithModuleSchema>;
export type TaskWithSprint = z.infer<typeof TaskWithSprintSchema>;
export type TaskWithRelations = z.infer<typeof TaskWithRelationsSchema>;

// Task response schemas using factories
export const TaskResponseSchema = createResponseSchema(TaskSchema);
export const TasksResponseSchema = createPaginatedResponseSchema(TaskSchema);

export type TaskResponse = z.infer<typeof TaskResponseSchema>;
export type TasksResponse = z.infer<typeof TasksResponseSchema>;

// Type guards
export function isValidTask(task: unknown): task is Task {
  if (!task || typeof task !== 'object') return false;
  
  const taskObj = task as Partial<Task>;
  
  return (
    typeof taskObj.id === 'string' &&
    typeof taskObj.organizationId === 'string' &&
    typeof taskObj.name === 'string' &&
    typeof taskObj.status === 'string' &&
    typeof taskObj.priority === 'string' &&
    typeof taskObj.order === 'string' &&
    typeof taskObj.isArchived === 'boolean'
  );
} 
